/**
 * Жизненный цикл тех. операции — подписи и действия для UI (AK-525).
 *
 * Источник правды по ГРАФУ переходов — бэкенд
 * (`backend/src/ikolhoz/services/operation_lifecycle.py`, AK-524). Каждая
 * операция приходит с полем `allowed_lifecycle_states` — списком состояний,
 * куда её можно перевести прямо сейчас. Кнопки рисуются ровно по нему, чтобы
 * фронт физически не мог предложить переход, который сервер отвергнет
 * («толкай и жди 422» — как раз то, что CLAUDE.md называет усталостью).
 *
 * Здесь — только человеческий слой: как назвать кнопку «pending → in_progress»
 * («Начать»), нужен ли вопрос перед действием, какой у кнопки вид.
 */

/** Значения `operations.lifecycle_state` (CHECK в БД + LIFECYCLE_VALUES на бэке). */
export type LifecycleState = 'pending' | 'in_progress' | 'done' | 'cancelled';

export const LIFECYCLE_LABELS: Record<LifecycleState, string> = {
  pending: 'Запланирована',
  in_progress: 'Выполняется',
  done: 'Выполнена',
  cancelled: 'Отменена',
};

/**
 * Резервная копия графа — используется ТОЛЬКО если бэкенд не прислал
 * `allowed_lifecycle_states` (старая сборка API в момент выката фронта).
 * Не «второй источник правды»: как только поле приходит, оно побеждает.
 */
const FALLBACK_TRANSITIONS: Record<LifecycleState, LifecycleState[]> = {
  pending: ['in_progress', 'done', 'cancelled'],
  in_progress: ['done', 'pending', 'cancelled'],
  done: ['in_progress'],
  cancelled: ['pending'],
};

export interface StatusAction {
  /** Целевое состояние. */
  target: LifecycleState;
  /** Подпись кнопки — глагол, а не название статуса («Начать», не «Выполняется»). */
  label: string;
  /** Вид кнопки из `@/components/ui/button`. */
  variant: 'default' | 'outline' | 'destructive' | 'secondary';
  /**
   * Текст подтверждения. Задан только у действий, которые пользователь может
   * нажать по ошибке и потерять на этом данные/контекст (отмена, откат).
   */
  confirm?: string;
}

/**
 * Подписи переходов. Ключ — «откуда→куда»: одна и та же цель называется
 * по-разному в зависимости от исходного состояния (`→ pending` это «Вернуть в
 * план» из «выполняется», но «Восстановить» из «отменена»).
 */
const ACTIONS: Record<string, Omit<StatusAction, 'target'>> = {
  'pending→in_progress': { label: 'Начать', variant: 'default' },
  'pending→done': { label: 'Отметить выполненной', variant: 'default' },
  'pending→cancelled': {
    label: 'Отменить',
    variant: 'outline',
    confirm: 'Отменить операцию? Её можно будет восстановить.',
  },
  'in_progress→done': { label: 'Завершить', variant: 'default' },
  'in_progress→pending': {
    label: 'Вернуть в план',
    variant: 'outline',
    confirm: 'Вернуть операцию в план? Фактические данные сохранятся.',
  },
  'in_progress→cancelled': {
    label: 'Отменить',
    variant: 'outline',
    confirm: 'Отменить операцию? Её можно будет восстановить.',
  },
  'done→in_progress': {
    label: 'Переоткрыть',
    variant: 'outline',
    confirm: 'Переоткрыть выполненную операцию для правки факта?',
  },
  'cancelled→pending': { label: 'Восстановить', variant: 'outline' },
};

/**
 * Какие кнопки смены статуса показать для операции.
 *
 * @param current текущее состояние
 * @param allowed `allowed_lifecycle_states` из API (если пусто — fallback-граф)
 */
export function statusActions(
  current: string | null | undefined,
  allowed?: string[] | null,
): StatusAction[] {
  const from = (current ?? '') as LifecycleState;
  const targets =
    allowed && allowed.length > 0
      ? (allowed as LifecycleState[])
      : (FALLBACK_TRANSITIONS[from] ?? []);

  return targets
    .map((target) => {
      const meta = ACTIONS[`${from}→${target}`];
      return meta ? { target, ...meta } : null;
    })
    .filter((a): a is StatusAction => a !== null);
}

/** Человеческая подпись состояния (для мест, где нет display_status-бейджа). */
export function lifecycleLabel(state?: string | null): string {
  return LIFECYCLE_LABELS[(state ?? '') as LifecycleState] ?? (state ?? '—');
}
